import {
  Component,
  OnInit,
  Inject,
  ViewChild,
  HostListener,
} from "@angular/core";
import { Location } from "@angular/common";
import { StandardService } from "../services/standard.service";
import { Standard } from "../models/standard";
import { Router, ActivatedRoute } from "@angular/router";
import {
  Constant,
  StandardTypeEnum,
  StandardListType,
  UserRoleUserOperation,
  AddendumStatusEnum,
} from "../core/constant";
import { Addendum } from "../models/addendum";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { DeleteDialogComponent } from "../delete-dialog/delete-dialog.component";
import { WebStorageService, LOCAL_STORAGE } from "ngx-webstorage-service";
import { FilteringGuide } from "../models/filteringGuide";
import { CryptographyService } from "../services/cryptography.service";
import { MatMenuTrigger } from "@angular/material/menu";
import { StandardList } from "../models/standard-list";
import { SelectedStandard } from "../models/selected-standard";
import { environment } from "../../environments/environment";
import { EquipmentStandard } from "../models/equipment-standard";
import { EquipmentStandardService } from "../services/equipment -standard.service";
import { FailModuleComponent } from "../fail-module/fail-module.component";
import { debug } from "util";
import { StandardType } from "../core/constant";
import { Sort } from "@angular/material/sort";

@Component({
  selector: "app-standards-search",
  templateUrl: "./standards-search.component.html",
  styleUrls: ["./standards-search.component.scss"],
})
export class StandardsSearchComponent implements OnInit {
  query: string = "";
  standardType: number = 0;
  loading: boolean = false;
  appliedFilters: any = [];
  selected = "option2";
  // Standard[] for every tab except "all", where matching exceptions are
  // merged in too (see applyClientFilters()) — hence any[].
  grses: any[] = [];
  // Full, unfiltered-by-category/location result set for the current
  // search+tab. Category/Location filtering and pagination are both done
  // client-side over this array (see applyClientFilters()).
  allGrses: Standard[] = [];

  // Exceptions tab: matching addendums now come back from the same
  // SearchAllStandards response as the standards (see getStandards()), so
  // this is just paginated client-side, same as allGrses/grses above.
  // Raw API shape (not the Addendum model's array-wrapped `country`), so `any[]`.
  allExceptions: any[] = [];
  exceptionResults: any[] = [];

  error: string = "";
  pageSizes: any[] = Constant.PageSizes;
  isDownloading: boolean = false;
  selectedRowsForExport: number[] = [];
  activePopupId: number | null = null;
  @ViewChild(MatMenuTrigger, { static: false }) trigger: MatMenuTrigger;

  page: number = 1;
  pageSize: number = 10;
  collectionSize: number = 0;
  totalPages: number = 0;
  userRole: number;
  get standardTypeEnum() {
    return StandardTypeEnum;
  }
  get currentResultCount(): number {
    return this.activeSearchTab === "exceptions"
      ? this.exceptionResults.length
      : this.grses.length;
  }
  get displayResults(): any[] {
    return this.activeSearchTab === "exceptions" ? this.exceptionResults : this.grses;
  }
  filterList: FilteringGuide[] = [];

  isPageEnabled: boolean = true;
  canEditEquipmentStandard: boolean;
  canDeleteEquipmentStandard: boolean;
  activeRoute: ActivatedRoute;

  // Search category tabs. Values line up with app-standard-search-filter's
  // filterOptions ids (0 All / 1 Global Building / 2 Global Equipment /
  // 3 Global Operation / 4 Global Approved Solutions / 5 Upcoming), which is
  // the same "type" query param the home-page search box already sends here.
  searchTabs: { id: string; label: string; icon: string }[] = [
    { id: "all", label: "All Results", icon: "/assets/images/list 1.svg" },
    {
      id: "standards",
      label: "Standards",
      icon: "/assets/images/Standard.svg",
    },
    {
      id: "equipment",
      label: "Equipment",
      icon: "/assets/images/Group (6).svg",
    },
    {
      id: "globalApprovedSolution",
      label: "Global Approved Solution",
      icon: "/assets/images/global approved icon.svg",
    },
    {
      id: "exceptions",
      label: "Exceptions",
      icon: "/assets/images/todo 2.svg",
    },
    {
      id: "marketStandards",
      label: "Market Standards",
      icon: "/assets/images/shop-pin 1.svg",
    },
  ];
  activeSearchTab: string = "all";

  // Tab -> standardType sent to the search API. Tabs left out here (Exceptions,
  // Market Standards) have no equivalent standardType yet, so clicking them
  // only highlights the tab without changing the results.
  tabToStandardType: { [tabId: string]: number } = {
    all: 0,
    standards: 1,
    equipment: 2,
    globalApprovedSolution: 4,
  };

  // Reverse of the above, used to preselect a tab when arriving with a
  // ?type= query param (e.g. from the home-page search filters).
  standardTypeToTab: { [type: number]: string } = {
    0: "all",
    1: "standards",
    2: "equipment",
    3: "standards",
    4: "globalApprovedSolution",
    5: "all",
  };

  // item.standardTypeId (per getStandardTypeName, 1-8) -> matching tab id,
  // used to show the same icon in the Type column as its tab uses.
  standardTypeIdToTab: { [standardTypeId: number]: string } = {
    1: "globalApprovedSolution",
    2: "marketStandards",
    3: "marketStandards",
    4: "standards",
    5: "equipment",
    6: "equipment",
    7: "standards",
    8: "standards",
  };

  getStandardTypeIcon(standardTypeId: number): string {
    const tabId = this.standardTypeIdToTab[standardTypeId] || "standards";
    const tab = this.searchTabs.find((t) => t.id === tabId);
    return tab ? tab.icon : "";
  }

  constructor(
    private standardService: StandardService,
    private router: Router,
    private ngbModal: NgbModal,
    @Inject(LOCAL_STORAGE)
    private storage: WebStorageService,
    private activatedRoute: ActivatedRoute,
    private cryptographyService: CryptographyService,
    private equipmentStandardService: EquipmentStandardService,
    private location: Location
  ) {}

  goBack(): void {
    this.location.back();
  }

  setActiveTab(tabId: string): void {
    this.activeSearchTab = tabId;
    const type = this.tabToStandardType[tabId];
    if (type !== undefined) {
      this.standardType = type;
      this.page = 1;
      this.getStandards();
    } else if (tabId === "marketStandards") {
      // No dedicated standardType for Market Standards on the API — fetch the
      // broad "All" set and narrow to Market types client-side instead.
      this.standardType = 0;
      this.page = 1;
      this.getStandards();
    } else if (tabId === "exceptions") {
      // allExceptions is already populated from the last getStandards() call
      // (the search API now returns matching addendums alongside standards).
      this.page = 1;
      this.applyExceptionFilters();
    }
  }

  ngOnInit() {
    this.filterList = this.standardService.allStandardsFiltration.filterSet;
    this.pageSize = this.standardService.searchAllFiltration.pageSize || 10;
    this.userRole = this.cryptographyService.decryptData(
      this.storage.get("userRole")
    );
    this.setUserPrivilages();
    this.activatedRoute.queryParams.subscribe((params) => {
      this.query = params["query"] || "";
      this.standardType =
        params["type"] != null && params["type"] !== ""
          ? Number(params["type"])
          : 0;
      this.activeSearchTab = this.standardTypeToTab[this.standardType] ?? "all";
      this.page = 1;
      if (this.query) {
        this.getStandards();
      } else {
        this.grses = [];
        this.collectionSize = 0;
        this.totalPages = 0;
        this.loading = false;
      }
    });
  }

  selectedItemsPerPageChange(event) {
    this.page = 1;
    this.pageSize = event.value;
    this.refreshCurrentTabResults();
  }

  /** Re-applies the current page/pageSize (and, for Standards tabs, the
   *  Category/Location filters) to whichever result set the active tab uses. */
  refreshCurrentTabResults(): void {
    if (this.activeSearchTab === "exceptions") {
      this.applyExceptionFilters();
    } else {
      this.applyClientFilters();
    }
  }

  getStandards(): void {
    if (!this.query) {
      this.grses = [];
      this.allGrses = [];
      this.collectionSize = 0;
      this.totalPages = 0;
      this.loading = false;
      return;
    }

    this.filterList = this.standardService.allStandardsFiltration.filterSet;
    this.loading = true;
    this.standardService.searchAllFiltration.pageSize = this.pageSize;
    // Fetch every match for the search+tab (category/location filtering and
    // pagination happen client-side below), rather than asking the server
    // to filter/paginate.
    this.standardService
      .searchAllStandards(
        0,
        5000,
        Number(this.standardType) || 0,
        null,
        null,
        this.query
      )
      .subscribe({
        next: (data: StandardList) => {
          this.allGrses = data.standardDetailList.map((o) => new Standard(o));
          // Tagged so the merged "All Results" list (see applyClientFilters())
          // and the shared table template can tell exceptions apart from
          // standards per-row, instead of relying on which tab is active.
          this.allExceptions = (data.addendums || []).map((a: any) => ({
            ...a,
            __isException: true,
          }));
          if (this.activeSearchTab === "exceptions") {
            this.applyExceptionFilters();
          } else {
            this.applyClientFilters();
          }
          this.loading = false;
        },
        error: () => {
          this.error =
            "Search request timed out. Please refine your search and try again.";
          this.grses = [];
          this.allGrses = [];
          this.exceptionResults = [];
          this.allExceptions = [];
          this.collectionSize = 0;
          this.totalPages = 0;
          this.loading = false;
          const modalRef = this.ngbModal.open(FailModuleComponent);
          modalRef.componentInstance.errorMessage = this.error;
        },
      });
  }

  /** allExceptions is already keyword-matched server-side (SearchAllStandards
   *  now returns matching addendums alongside standards) — this just paginates it. */
  applyExceptionFilters(): void {
    this.collectionSize = this.allExceptions.length;
    this.totalPages = Math.ceil(this.collectionSize / this.pageSize) || 0;
    const start = (this.page - 1) * this.pageSize;
    this.exceptionResults = this.allExceptions.slice(start, start + this.pageSize);
  }

  exceptionRowSelected(addendum: Addendum): void {
    if (addendum.addendumStatus?.id === AddendumStatusEnum.DraftBeforeRequest) {
      this.router.navigate(["/add-addendum/" + addendum.standard.id]);
    } else {
      this.router.navigate(["/addendum/" + addendum.id]);
    }
  }

  /** Selected filter ids (per FilteringEnum) currently applied, e.g. category=2, location=1. */
  private getFilterIds(filterType: number): number[] {
    const guide = (this.filterList || []).find((f) => f.type === filterType);
    return guide ? guide.ids : [];
  }

  /** Applies the tab (when not already handled server-side)/Category/Location filters and current page/pageSize to allGrses, client-side. */
  applyClientFilters(): void {
    const categoryIds = this.getFilterIds(2);
    const locationIds = this.getFilterIds(1);

    let filtered = this.allGrses;
    if (
      this.activeSearchTab === "marketStandards" &&
      this.tabToStandardType[this.activeSearchTab] === undefined
    ) {
      filtered = filtered.filter(
        (item) => this.standardTypeIdToTab[item.standardTypeId] === "marketStandards"
      );
    }
    if (categoryIds.length > 0) {
      filtered = filtered.filter((item) => categoryIds.includes(item.categoryId));
    }
    if (locationIds.length > 0) {
      filtered = filtered.filter((item) => locationIds.includes(item.locationTypeId));
    }

    // "All Results" also includes matching exceptions, same as the Exceptions
    // tab shows them — Category/Location filters don't apply to them (the
    // exceptions tab doesn't expose those filters either).
    let combined: any[] = filtered;
    if (this.activeSearchTab === "all") {
      combined = [...filtered, ...this.allExceptions];
    }

    this.collectionSize = combined.length;
    this.totalPages = Math.ceil(this.collectionSize / this.pageSize) || 0;
    const start = (this.page - 1) * this.pageSize;
    this.grses = combined.slice(start, start + this.pageSize);
  }

  compare(
    a: number | string | Date,
    b: number | string | Date,
    isAsc: boolean
  ) {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  sortData(sort: Sort) {
    if (!sort.active || sort.direction === "" || !(this.grses.length > 0)) {
      return;
    }

    const isAsc = sort.direction === "asc";
    this.grses.sort((a, b) => {
      switch (sort.active) {
        case "title":
          return this.compare(a.title, b.title, isAsc);
        case "category":
          return this.compare(a.category.name, b.category.name, isAsc);
        case "desc":
          return this.compare(a.shortDescription, b.shortDescription, isAsc);
      }
    });
  }

  setUserPrivilages() {
    if (!environment.skipAuthentication) {
      let userRoleUserOperation = this.storage.get("userRoleUserOperation")
        ? this.cryptographyService.decryptData(
            this.storage.get("userRoleUserOperation")
          )
        : this.storage.get("userRoleUserOperation");
      if (userRoleUserOperation) {
        this.canEditEquipmentStandard =
          userRoleUserOperation.indexOf(
            UserRoleUserOperation.CanEditEquipmentStandard
          ) !== -1;
        this.canDeleteEquipmentStandard =
          userRoleUserOperation.indexOf(
            UserRoleUserOperation.CanDeleteEquipmentStandard
          ) !== -1;
      }
    }
  }
  rowSelected(item: Standard): void {
    const currdate = new Date();
    console.log("curr date", currdate)
     
    const effectiveDate = new Date(item.effectiveFrom);
    console.log("effective", effectiveDate)
    if(effectiveDate > currdate){
      this.router.navigate(["/view-upcoming-standard-detail/" + item.id])
      return 
    }

    switch (item.standardTypeId) {
      case 1:
        this.router.navigate([
          "/global-recommendations-detail-view/" + item.id,
        ]);
        break;

      case 3:
        this.router.navigate(["/nrbes-view/" + item.id]);
        break;
      case 4:
        this.router.navigate(["/minimum-standard-detail-view/" + item.id]);
        break;
      case 5:
        this.router.navigate(["/view-equipments-standard-detail/" + item.id]);
        break;
      case 6:
          this.router.navigate(["/view-national-equipments-standard-detail/" + item.id]);
          break;  

      case 7:
        this.router.navigate(["/nrbes-view/" + item.id]);
        break;

      case 8:
        this.router.navigate(["/operations-standards-detail-view/" + item.id]);
        break;
    }
  }

  openDeleteModal(item, event) {
    event.stopPropagation();
    this.activePopupId = null;
    const modalRef = this.ngbModal.open(DeleteDialogComponent);
    modalRef.componentInstance.title =
      "Do you want to remove this standard from the list? ";
    modalRef.componentInstance.deleteItem = item;
    if (
      item.standardTypeId == StandardTypeEnum.GlobalMinimumRestaurantStandard ||
      item.standardTypeId == StandardTypeEnum.GlobalEquipmentStandard ||
      StandardTypeEnum.OperationsStandards
    ) {
      modalRef.componentInstance.message =
        "All the dependencies will be removed along with this standard";
    }
    this.trigger.closeMenu();
    modalRef.componentInstance.deletePopupEmit.subscribe((deleteItem) => {
      if (deleteItem != null) {
        if (item.standardTypeId == StandardTypeEnum.GlobalEquipmentStandard) {
          let equipmentStandard = new EquipmentStandard();
          equipmentStandard.id = deleteItem.equipmentStandard.id;
          equipmentStandard.isDeleted = true;
          this.loading = true;
          this.equipmentStandardService
            .updateEquipmentStandard(equipmentStandard)
            .subscribe((data: any) => {
              this.loading = false;
              this.page = 1;
              this.getStandards();
            });
        } else {
          let grs = new SelectedStandard();
          grs.standard.id = deleteItem.id;
          grs.standard.isDeleted = true;
          this.loading = true;
          this.standardService
            .removeGrbes(grs.standard)
            .subscribe((data: any) => {
              this.loading = false;
              this.page = 1;
              this.getStandards();
            });
        }
      }
      this.ngbModal.dismissAll();
    });
  }

  actionMenuOpen(event) {
    event.stopPropagation();
  }

  togglePopup(event: Event, item: Standard) {
    event.stopPropagation();
    this.activePopupId = this.activePopupId === item.id ? null : item.id;
  }

  @HostListener("document:click", ["$event"])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest(".grid-actions")) {
      this.activePopupId = null;
    }
  }

  receivedFilteration($event) {
    this.isPageEnabled = false;
    this.filterList = $event;
    this.standardService.allStandardsFiltration.filterSet = this.filterList;
    this.page = 1;
    this.applyClientFilters();
  }

  closeMatMenuAndOpenDeleteModal(item, event) {
    this.trigger.closeMenu();
    setTimeout(() => {
      this.openDeleteModal(item, event);
    }, 100);
  }

  onEdit(item) {
    this.activePopupId = null;
    this.standardService.standardEditPreviousRoute =
      this.activatedRoute.snapshot.routeConfig.path;
    if (item.standardType.id != this.standardTypeEnum.GlobalEquipmentStandard) {
      this.router.navigate(["/add-grs", item.id]);
    } else {
      this.router.navigate(["/edit-eq-standards-global", item.id]);
    }
  }

  downloadExcel() {
    this.isDownloading = true;
    this.standardService
      .downloadExcel(
        0,
        this.collectionSize,
        StandardListType.Upcoming,
        this.filterList,
        null,
        null
      )
      .subscribe(
        (response: any) => {
          let filename =
            "Upcoming_Standard_List " + new Date().toLocaleString();
          this.standardService.saveExportBlob(response, filename);
          this.isDownloading = false;

          //window.open(url);
        },
        (error: any) => {
          this.isDownloading = false;
          const modalRef = this.ngbModal.open(FailModuleComponent);
          modalRef.componentInstance.errorMessage = error.error
            ? error.error.Message
            : error.message;
        }
      );
  }

  isSelected(id: number): boolean {
    return this.selectedRowsForExport.includes(id);
  }

  isAllOnPageSelected(): boolean {
    if (this.grses.length === 0) {
      return false;
    }
    return this.grses.every((item) => this.isSelected(item.id));
  }

  isSomeOnPageSelected(): boolean {
    const selectedCount = this.grses.filter((item) =>
      this.isSelected(item.id)
    ).length;
    return selectedCount > 0 && selectedCount < this.grses.length;
  }

  selectAllOnPageChange(event: any): void {
    if (event.checked) {
      this.grses.forEach((item) => {
        if (!this.selectedRowsForExport.includes(item.id)) {
          this.selectedRowsForExport.push(item.id);
        }
      });
    } else {
      this.grses.forEach((item) => {
        const index = this.selectedRowsForExport.indexOf(item.id);
        if (index > -1) {
          this.selectedRowsForExport.splice(index, 1);
        }
      });
    }
  }

  onCheckboxChange(event: any, id: number): void {
    if (event.checked) {
      if (!this.selectedRowsForExport.includes(id)) {
        this.selectedRowsForExport.push(id);
      }
    } else {
      const index = this.selectedRowsForExport.indexOf(id);
      if (index > -1) {
        this.selectedRowsForExport.splice(index, 1);
      }
    }
  }
  goToNextPage() {
    if (this.page < this.totalPages) {
      this.page++;
      this.refreshCurrentTabResults();
    }
  }

  goToPreviousPage() {
    if (this.page > 1) {
      this.page--;
      this.refreshCurrentTabResults();
    }
  }

  getLocationNames(item): any {
    if (item.standardLocation && item.standardLocation.length > 0) {
      return item.standardLocation
        .map((loc) => loc.locationType.name)
        .join(", ");
    } else {
      return item.locationType.name;
    }
  }

  getStandardTypeName(id: number): string {
    let standardTYpeName: string = "";
      switch (id) {
        case 1:
          return (standardTYpeName = "Global Approved Solution");
        case 2:
          return (standardTYpeName = "Market Best Practices");
        case 3:
          return (standardTYpeName = "Market Standard");
        case 4:
          return (standardTYpeName = "Global Building Restaurant Standard");
        case 5:
          return (standardTYpeName = "Global Equipment Standard");
        case 6:
          return (standardTYpeName = "Local Equipment Standard");
        case 7:
          return (standardTYpeName = "BU Standard");
        case 8:
          return (standardTYpeName = "Global Operation Standard");
      }
  }
}
